import React from "react";
import { useState } from "react";
import Swal from 'sweetalert2'
import { apiHandler } from '../../api/apihandler';
import { BILLING_PAY_STRIPE } from '../../api/constants'
import LoaderModal from '../loaderModal'
import {
    CardElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';

// Mirrors StripeCardForGrade (the established tuition-payment Stripe flow)
// exactly, applied to a billing invoice balance instead of a grade fee - the
// backend call (BILLING_PAY_STRIPE) charges via the same Stripe
// customer/card pattern, then records the result through the real
// PaymentRecordingService (see Phase 12's BillingController.payStripe).
// This component never records a payment itself - only Stripe token
// collection and the one API call happen here.
function StripeCardForBilling({ amount, payFullBalance = false, onSuccess, onCancel }) {
    const stripe = useStripe();
    const elements = useElements();
    const [showLoader, setShowLoader] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const submitPayment = async () => {
        if (submitting) {
            // Guards against a duplicate frontend submission (e.g. a fast
            // double click) before the first request even resolves - the
            // backend's own idempotency key remains the real, authoritative
            // protection (Phase 7/12), this only avoids firing the request
            // itself twice.
            return;
        }
        setSubmitting(true);
        try {
            setShowLoader(true)
            const cardElement = elements.getElement(CardElement);
            const token = await stripe.createToken(cardElement);
            if (!token?.token?.id) {
                Swal.fire({
                    title: 'Error',
                    text: token?.error?.message || 'Your card details are not valid.',
                    icon: 'error',
                    confirmButtonText: 'Okay'
                })
                return;
            }

            const res = await apiHandler(BILLING_PAY_STRIPE, "POST", {
                amount: Number(amount).toFixed(2),
                // Server charges exactly the outstanding balance when set.
                ...(payFullBalance ? { pay_full_balance: true } : {}),
                token: token.token.id,
            })

            if (res?.isAxiosError || (res?.response && res?.data === undefined)) {
                Swal.fire({
                    title: 'Payment Failed',
                    text: res?.response?.data?.data?.message || res?.response?.data?.message || 'Unable to process payment. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'Okay'
                })
                return;
            }

            if (res.data?.response?.status === 200 || res.data?.response?.status === 201) {
                // pending_recording: charged but not yet recorded - show the
                // server's "do not pay again" message. Otherwise report what
                // the server recorded (pay_full_balance charges its own figure).
                const data = res.data?.data
                Swal.fire({
                    title: data?.pending_recording ? 'Payment Received' : 'Payment Successful',
                    text: data?.pending_recording
                        ? data.message
                        : `Your payment of $${Number(data?.payment?.amount ?? amount).toFixed(2)} was received.`,
                    icon: 'success',
                    confirmButtonText: 'Okay'
                })
                // Never claimed successful merely because the request was
                // sent - onSuccess only fires once the backend itself
                // confirmed the payment was recorded.
                onSuccess && onSuccess();
            } else {
                Swal.fire({
                    title: 'Payment Failed',
                    text: res?.data?.data?.message || 'Unable to process payment. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'Okay'
                })
            }
        } catch (error) {
            Swal.fire({
                title: 'Payment Failed',
                text: 'Something went wrong while processing your payment. Please try again.',
                icon: 'error',
                confirmButtonText: 'Okay'
            })
        } finally {
            setShowLoader(false)
            setSubmitting(false)
        }
    }

    return (
        <>
            <LoaderModal showLoader={showLoader} loaderData="Processing your payment..." />
            <div className="border rounded p-3 mb-3 bg-white">
                <CardElement options={{ style: { base: { fontSize: '16px' } } }} />
            </div>
            <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" disabled={submitting} onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={submitting || !stripe} onClick={submitPayment}>
                    {submitting ? 'Processing...' : `Pay $${Number(amount || 0).toFixed(2)}`}
                </button>
            </div>
        </>
    );
}
export default StripeCardForBilling;
